import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import mixpanel from "mixpanel-browser";

import { apiClient } from "@/api/config/api-client";
import { API_ENDPOINTS } from "@/api/config/api-endpoints";
import useAuthStore from "@/store/use-auth-store";
import { useToast } from "@/features/toast";

import {
  CONTACT_SOURCE,
  MIN_MESSAGE_LENGTH,
  buildContactCategory,
  buildContactMessage,
  buildContactSubject,
  findCategory,
  findIssue,
  supportCategories,
} from "../config/support-config";
import { useSupportStore } from "../store/support-store";
import { useImageAttachments } from "./use-image-attachments";
import type { SupportTicketResponse } from "../types";

export interface SupportFormValues {
  fullName: string;
  email: string;
  categoryId: string;
  issueId: string;
  modelName: string;
  message: string;
}

/**
 * Support contact formunun tüm business logic'i:
 * RHF + dinamik zod validasyonu, R2 görsel yükleme, send-contact mutation'ı.
 */
export const useSupportForm = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { success, error } = useToast();
  const { user } = useAuthStore();
  const { setLastSubmission } = useSupportStore();

  // 📎 Görsel ekleri (ortak validasyon hook'u)
  const {
    attachments,
    addAttachments,
    removeAttachment,
    clearAttachments,
    maxAttachments,
  } = useImageAttachments();

  const schema = useMemo(
    () =>
      z
        .object({
          fullName: z
            .string()
            .trim()
            .min(2, t("support.validation.fullNameRequired")),
          email: z
            .string()
            .trim()
            .email(t("support.validation.emailInvalid")),
          categoryId: z
            .string()
            .min(1, t("support.validation.categoryRequired")),
          issueId: z.string().min(1, t("support.validation.issueRequired")),
          modelName: z.string(),
          message: z.string(),
        })
        .superRefine((values, ctx) => {
          const issue = findIssue(values.categoryId, values.issueId);
          if (!issue) return;

          if (issue.model === "required" && !values.modelName) {
            ctx.addIssue({
              code: "custom",
              path: ["modelName"],
              message: t("support.validation.modelRequired"),
            });
          }

          const message = values.message.trim();
          const isMessageRequired = (issue.message ?? "optional") === "required";

          if (isMessageRequired && message.length === 0) {
            ctx.addIssue({
              code: "custom",
              path: ["message"],
              message: t("support.validation.messageRequired", {
                min: MIN_MESSAGE_LENGTH,
              }),
            });
          } else if (message.length > 0 && message.length < MIN_MESSAGE_LENGTH) {
            // Backend mesajı 10 karakterden kısa kabul etmiyor
            ctx.addIssue({
              code: "custom",
              path: ["message"],
              message: t("support.validation.messageTooShort", {
                min: MIN_MESSAGE_LENGTH,
              }),
            });
          }
        }),
    [t],
  );

  const form = useForm<SupportFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      fullName: "",
      email: "",
      categoryId: "",
      issueId: "",
      modelName: "",
      message: "",
    },
  });

  const { watch, setValue, reset } = form;

  const categoryId = watch("categoryId");
  const issueId = watch("issueId");

  const selectedCategory = useMemo(
    () => findCategory(categoryId),
    [categoryId],
  );
  const selectedIssue = useMemo(
    () => findIssue(categoryId, issueId),
    [categoryId, issueId],
  );

  const showModelField = !!selectedIssue?.model;
  const attachmentsAllowed = selectedIssue?.attachments !== false;
  const messageRequired = selectedIssue?.message === "required";

  // 🔐 Login olan kullanıcı için ad/e-posta otomatik dolar ve kilitlenir
  const isLoggedIn = !!user?._id;
  useEffect(() => {
    if (user?.email) {
      setValue("email", user.email, { shouldValidate: false });
    }
    if (user?.name) {
      setValue("fullName", user.name, { shouldValidate: false });
    }
  }, [user?.email, user?.name, setValue]);

  // Kategori değişince madde ve model seçimini sıfırla
  const handleCategoryChange = (nextCategoryId: string) => {
    setValue("categoryId", nextCategoryId, { shouldValidate: true });
    setValue("issueId", "", { shouldValidate: false });
    setValue("modelName", "", { shouldValidate: false });
  };

  const handleIssueChange = (nextIssueId: string) => {
    setValue("issueId", nextIssueId, { shouldValidate: true });
    setValue("modelName", "", { shouldValidate: false });

    // Eklere izin vermeyen maddeye geçilince mevcut ekleri temizle
    const nextIssue = findIssue(categoryId, nextIssueId);
    if (nextIssue?.attachments === false) {
      clearAttachments();
    }
  };

  const mutation = useMutation({
    mutationFn: (payload: FormData) =>
      apiClient.post<SupportTicketResponse>(API_ENDPOINTS.sendContact, payload),
  });

  const onSubmit = form.handleSubmit(async (values) => {
    const category = findCategory(values.categoryId);
    const issue = findIssue(values.categoryId, values.issueId);
    if (!category || !issue) return;

    try {
      // Tek atomik istek: görseller dosya olarak gönderilir, R2 yüklemesini
      // backend yapar. İstek başarısız olursa orphan asset oluşmaz.
      const message = buildContactMessage(category, issue, values.message);

      const formData = new FormData();
      formData.append("fullName", values.fullName.trim());
      formData.append("email", values.email.trim());
      formData.append("subject", buildContactSubject(category, issue));
      // send-contact DTO'su `message`, güncel dokümantasyon `body` diyor;
      // iki alan da aynı içerikle gönderilir, backend hangisini okursa okusun
      formData.append("message", message);
      formData.append("body", message);
      formData.append("category", buildContactCategory(category));
      formData.append("source", CONTACT_SOURCE);

      const locale = i18n.language?.split("-")[0];
      if (locale) {
        formData.append("locale", locale);
      }
      if (values.modelName) {
        formData.append("model", values.modelName);
      }

      attachments.forEach((file) => formData.append("images", file));

      const response = await mutation.mutateAsync(formData);

      if (!response?.success) {
        error(response?.message || t("support.form.submitError"));
        return;
      }

      // 3. Success ekranı + analytics
      mixpanel.track("Support Request Submitted", {
        category: category.emailLabel,
        issue: issue.emailLabel,
        has_attachments: attachments.length > 0,
        is_logged_in: isLoggedIn,
      });

      success(t("support.form.submitSuccess"));
      reset({
        fullName: user?.name ?? "",
        email: user?.email ?? "",
        categoryId: "",
        issueId: "",
        modelName: "",
        message: "",
      });
      clearAttachments();

      if (isLoggedIn && response.data?._id) {
        // Login kullanıcı: doğrudan ticket detay (konuşma) sayfasına git
        navigate(`/support/tickets/${response.data._id}`);
      } else {
        // Misafir: detaya erişemez (magic link maille gelir) → success kartı
        setLastSubmission({
          email: values.email.trim(),
          categoryLabelKey: category.labelKey,
          issueLabelKey: issue.labelKey,
          emailSent: response.data?.emailSent ?? true,
          ticketId: response.data?._id,
          ticketNumber: response.data?.ticketNumber,
        });
      }
    } catch (submitError) {
      console.error("Support form submit failed:", submitError);
      const apiError = submitError as {
        response?: { data?: { message?: string } };
        message?: string;
      };
      error(
        apiError?.response?.data?.message ||
          apiError?.message ||
          t("support.form.submitError"),
      );
    }
  });

  return {
    // Form
    form,
    onSubmit,
    isSubmitting: mutation.isPending,

    // Config & selections
    categories: supportCategories,
    selectedCategory,
    selectedIssue,
    showModelField,
    attachmentsAllowed,
    messageRequired,
    handleCategoryChange,
    handleIssueChange,

    // Attachments
    attachments,
    addAttachments,
    removeAttachment,
    maxAttachments,

    // Auth
    isLoggedIn,
    user,
  };
};
